import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Filter, Download, Trash2, AlertTriangle, Info, AlertCircle, Bug, Zap, Calendar, User, Activity } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface SystemLog {
  id: string;
  log_level: string;
  module: string;
  action?: string;
  message: string;
  details?: any;
  worker_id?: string;
  task_id?: string;
  created_at: string;
}

interface LogStatistics {
  total: number;
  today: number;
  errors: number;
  byLevel: { [key: string]: number };
  byModule: { [key: string]: number };
  hourlyErrors: { [key: string]: number };
}

const ScrapeLogViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'logs' | 'statistics'>('logs');
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [statistics, setStatistics] = useState<LogStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLog, setSelectedLog] = useState<SystemLog | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);

  const logLevels = [
    { value: 'DEBUG', label: '调试', icon: Bug, color: 'gray' },
    { value: 'INFO', label: '信息', icon: Info, color: 'blue' },
    { value: 'WARNING', label: '警告', icon: AlertTriangle, color: 'yellow' },
    { value: 'ERROR', label: '错误', icon: AlertCircle, color: 'red' },
    { value: 'CRITICAL', label: '严重', icon: Zap, color: 'purple' }
  ];

  const modules = [
    'scraper',
    'scheduler',
    'worker',
    'database',
    'session',
    'template',
    'system'
  ];

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=log_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 1000,
          log_level: levelFilter !== 'all' ? levelFilter : undefined,
          module: moduleFilter !== 'all' ? moduleFilter : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          search: searchTerm || undefined
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setLogs(result.data.data || []);
      } else {
        console.error('获取系统日志失败:', result.error);
        toast.error('获取系统日志失败');
      }
    } catch (error) {
      console.error('获取系统日志失败:', error);
      toast.error('获取系统日志失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=log_statistics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setStatistics(result.data);
      } else {
        console.error('获取日志统计失败:', result.error);
        toast.error('获取日志统计失败');
      }
    } catch (error) {
      console.error('获取日志统计失败:', error);
      toast.error('获取日志统计失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLog = async (id: string) => {
    if (!confirm('确定要删除这条日志吗？')) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=log_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('日志删除成功');
      await fetchLogs();
    } catch (error) {
      console.error('删除日志失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleCleanupOldLogs = async () => {
    const days = prompt('请输入要清理多少天前的日志（1-365）:', '30');
    if (!days) return;

    const daysNum = parseInt(days);
    if (isNaN(daysNum) || daysNum < 1 || daysNum > 365) {
      toast.error('请输入有效的天数（1-365）');
      return;
    }

    if (!confirm(`确定要清理 ${daysNum} 天前的日志吗？此操作不可撤销。`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=log_cleanup&days=${daysNum}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(result.data.message);
      await fetchLogs();
    } catch (error) {
      console.error('清理旧日志失败:', error);
      toast.error('清理失败：' + (error as Error).message);
    }
  };

  const handleExportLogs = async () => {
    try {
      const filteredLogs = logs.filter(log => {
        const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             log.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (log.action || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      });

      const csvHeaders = ['时间', '级别', '模块', '操作', '消息', 'Worker ID', '任务ID'];
      const csvRows = filteredLogs.map(log => [
        new Date(log.created_at).toLocaleString(),
        log.log_level,
        log.module,
        log.action || '',
        log.message.replace(/"/g, '""'), // 转义双引号
        log.worker_id || '',
        log.task_id || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      const dataBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scrape_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('日志导出成功');
    } catch (error) {
      console.error('导出日志失败:', error);
      toast.error('导出失败：' + (error as Error).message);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (log.action || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getLevelInfo = (level: string) => {
    return logLevels.find(l => l.value === level) || logLevels[1];
  };

  const formatLogTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Activity className="w-6 h-6 mr-2 text-blue-600" />
              系统日志查看
            </h2>
            <div className="flex items-center space-x-3">
              {activeTab === 'logs' && (
                <>
                  <button
                    onClick={handleCleanupOldLogs}
                    className="flex items-center px-3 py-2 text-orange-600 border border-orange-300 rounded-md hover:bg-orange-50 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    清理旧日志
                  </button>
                  <button
                    onClick={handleExportLogs}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    导出日志
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 标签页 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'logs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              系统日志
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'statistics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              统计分析
            </button>
          </nav>
        </div>
        
        <div className="p-6">
          {activeTab === 'logs' && (
            <>
              {/* 搜索和筛选 */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="搜索日志消息、模块或操作..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">全部级别</option>
                  {logLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
                <select
                  value={moduleFilter}
                  onChange={(e) => setModuleFilter(e.target.value)}
                  className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">全部模块</option>
                  {modules.map(module => (
                    <option key={module} value={module}>
                      {module}
                    </option>
                  ))}
                </select>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={fetchLogs}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Filter className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 日志列表 */}
              <div className="space-y-2">
                {filteredLogs.map((log) => {
                  const levelInfo = getLevelInfo(log.log_level);
                  const LevelIcon = levelInfo.icon;
                  
                  return (
                    <div key={log.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className={`p-1 rounded-full bg-${levelInfo.color}-100`}>
                            <LevelIcon className={`w-4 h-4 text-${levelInfo.color}-600`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${levelInfo.color}-100 text-${levelInfo.color}-800`}>
                                {levelInfo.label}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                {log.module}
                              </span>
                              {log.action && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {log.action}
                                </span>
                              )}
                              <div className="flex items-center text-xs text-gray-500">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatLogTime(log.created_at)}
                              </div>
                            </div>
                            <p className="text-sm text-gray-900 mb-2">{log.message}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              {log.worker_id && (
                                <div className="flex items-center">
                                  <User className="w-3 h-3 mr-1" />
                                  Worker: {log.worker_id}
                                </div>
                              )}
                              {log.task_id && (
                                <div className="flex items-center">
                                  <Activity className="w-3 h-3 mr-1" />
                                  Task: {log.task_id}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {log.details && (
                            <button
                              onClick={() => {
                                setSelectedLog(log);
                                setShowLogModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                              title="查看详情"
                            >
                              详情
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteLog(log.id)}
                            className="text-red-600 hover:text-red-900"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredLogs.length === 0 && (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">暂无系统日志</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'statistics' && statistics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 统计卡片 */}
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-blue-600">总日志数</p>
                    <p className="text-2xl font-bold text-blue-900">{statistics.total.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-green-600">今日日志</p>
                    <p className="text-2xl font-bold text-green-900">{statistics.today.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-red-600">错误日志</p>
                    <p className="text-2xl font-bold text-red-900">{statistics.errors.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-orange-600">错误率</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {statistics.total > 0 ? Math.round((statistics.errors / statistics.total) * 100) : 0}%
                    </p>
                  </div>
                </div>
              </div>

              {/* 按级别统计 */}
              {Object.keys(statistics.byLevel).length > 0 && (
                <div className="md:col-span-2 lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">按日志级别统计</h3>
                  <div className="space-y-3">
                    {Object.entries(statistics.byLevel).map(([level, count]) => {
                      const levelInfo = getLevelInfo(level);
                      const LevelIcon = levelInfo.icon;
                      return (
                        <div key={level} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <LevelIcon className={`w-4 h-4 mr-2 text-${levelInfo.color}-600`} />
                            <span className="text-sm text-gray-600">{levelInfo.label}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 按模块统计 */}
              {Object.keys(statistics.byModule).length > 0 && (
                <div className="md:col-span-2 lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">按模块统计</h3>
                  <div className="space-y-3">
                    {Object.entries(statistics.byModule).slice(0, 10).map(([module, count]) => (
                      <div key={module} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{module}</span>
                        <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 24小时错误趋势 */}
              {Object.keys(statistics.hourlyErrors).length > 0 && (
                <div className="md:col-span-2 lg:col-span-4 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">24小时错误趋势</h3>
                  <div className="grid grid-cols-12 gap-2">
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = `${i}:00`;
                      const count = statistics.hourlyErrors[hour] || 0;
                      const maxCount = Math.max(...Object.values(statistics.hourlyErrors));
                      const height = maxCount > 0 ? Math.max(4, (count / maxCount) * 40) : 4;
                      
                      return (
                        <div key={hour} className="flex flex-col items-center">
                          <div
                            className="bg-red-500 rounded-t"
                            style={{ height: `${height}px`, width: '20px' }}
                            title={`${hour}: ${count} 个错误`}
                          />
                          <div className="text-xs text-gray-500 mt-1">{i}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 日志详情弹窗 */}
      {showLogModal && selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">日志详情</h3>
                <button
                  onClick={() => setShowLogModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">日志级别</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {getLevelInfo(selectedLog.log_level).label}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">模块</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedLog.module}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">操作</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedLog.action || '未指定'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">时间</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {formatLogTime(selectedLog.created_at)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">消息</label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {selectedLog.message}
                  </div>
                </div>

                {selectedLog.worker_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Worker ID</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                      {selectedLog.worker_id}
                    </div>
                  </div>
                )}

                {selectedLog.task_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">任务ID</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                      {selectedLog.task_id}
                    </div>
                  </div>
                )}

                {selectedLog.details && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">详细信息</label>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="text-xs text-gray-700 overflow-auto max-h-64">
                        {JSON.stringify(selectedLog.details, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowLogModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapeLogViewer;